import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import Tab from '../../../components/common/Tab';
import InvoiceBatchTable from '../../../components/tables/InvoiceBatchTable';
import { useInvoiceBatchApi } from '../../../hooks/useInvoiceBatchApi';
import { useNetcashBatchApi } from '../../../hooks/useNetcashBatchApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete, confirmAction, showAlert } from '../../../utils/sweetAlertUtils';
import { InvoiceBatchDTO } from '../../../services/apiClient';
import { getApiErrorMessage, isClientError } from '../../../utils/apiErrorUtils';
import { describeNetcashAddResult } from '../../../utils/netcashAddSummary';
import { useNavigate } from 'react-router-dom';
import Drawer from '../../../components/common/Drawer';
import FormField from '../../../components/common/FormField';
import Button from '../../../components/common/Button';
import NetcashBatchesPanel from './NetcashBatchesPanel';
import AddToNetcashBatchDrawer, { NetcashBatchTarget } from '../../../components/drawer/AddToNetcashBatchDrawer';

const monthOptions = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' }
];

const InvoiceBatchesPanel: React.FC = () => {
  const { search, remove, extract, create, sendRenewalNotices, isLoading } = useInvoiceBatchApi();
  const { addFromInvoiceBatch, isLoading: isAddingToNetcashBatch } = useNetcashBatchApi();
  const [netcashDrawerBatch, setNetcashDrawerBatch] = useState<InvoiceBatchDTO | null>(null);
  const [items, setItems] = useState<InvoiceBatchDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const navigate = useNavigate();

  const fetchItems = async (page: number, size: number) => {
    try {
      const response = await search({
        filters: {},
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Invoice Batches:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const handleDelete = async (dto: InvoiceBatchDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize);
    } catch (error) {
      console.error('Error deleting InvoiceBatch:', error);
    }
  };

  const handleOpenDrawer = () => {
    setSelectedMonth(new Date().getMonth() + 1);
    setSelectedYear(new Date().getFullYear());
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleCreate = async () => {
    setIsSubmitting(true);
    try {
      const monthPadded = selectedMonth.toString().padStart(2, '0');
      const batchNo = parseInt(`${selectedYear}${monthPadded}`);

      const data: Partial<InvoiceBatchDTO> = {
        month: selectedMonth,
        year: selectedYear,
        batchNo: batchNo,
        totalCreated: 0,
        totalPaid: 0,
      };

      await create(data as InvoiceBatchDTO);
      await fetchItems(currentPage, pageSize);
      handleCloseDrawer();
    } catch (error) {
      console.error('Error creating Invoice Batch:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewBatchDetail = (batch: InvoiceBatchDTO) => {
    navigate(`/admin/invoicebatches/${batch.id}`);
  };

  const handleSendRenewalNotices = async (batch: InvoiceBatchDTO) => {
    const confirmed = await confirmAction({
      title: 'Send Renewal Notices?',
      text: `This will queue renewal notice emails for all invoices in this batch. The process will run in the background. Continue?`,
      confirmButtonText: 'Yes, Send Notices',
      cancelButtonText: 'Cancel',
      icon: 'question',
    });

    if (!confirmed) return;

    try {
      await sendRenewalNotices(batch.id || '');
      await showAlert({
        title: 'Processing Started',
        text: 'Renewal notices are being sent in the background. The batch status will be updated to SENT.',
        icon: 'success',
        confirmButtonText: 'OK',
      });
      await fetchItems(currentPage, pageSize);
    } catch (error) {
      console.error('Error sending renewal notices:', error);
      await showAlert({
        title: 'Error',
        // e.g. the batch is mid-regeneration (409) - show the server's reason
        text: getApiErrorMessage(error, 'Failed to queue renewal notices. Please try again.'),
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleAddToNetcashBatch = (batch: InvoiceBatchDTO) => {
    setNetcashDrawerBatch(batch);
  };

  const handleConfirmAddToNetcashBatch = async (target: NetcashBatchTarget) => {
    const batch = netcashDrawerBatch;
    if (!batch?.id) return;

    try {
      const response = await addFromInvoiceBatch(batch.id, {
        netcashBatchId: target.batchId,
        year: target.year,
        month: target.month,
      });
      const result = response?.data;
      const created = !target.batchId;

      setNetcashDrawerBatch(null);
      await showAlert({
        title: created ? 'Netcash Batch Created' : 'Added to Netcash Batch',
        text:
          `${describeNetcashAddResult(result)} ` +
          `The ${created ? 'new' : ''} draft Netcash batch now has ${result?.batch?.totalCount ?? 0} invoice(s) totalling R${(result?.batch?.totalAmount ?? 0).toFixed(2)}. ` +
          'Choose the collection date and push it from the Netcash Batches tab when ready.',
        icon: 'success',
        confirmButtonText: 'OK',
      });
    } catch (error: any) {
      console.error('Error adding invoice batch to Netcash batch:', error);
      await showAlert({
        title: isClientError(error) ? 'Nothing Added' : 'Error',
        text: getApiErrorMessage(error, 'Failed to add this batch to the Netcash batch. Please try again.'),
        icon: isClientError(error) ? 'warning' : 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <AddNewButton onClick={handleOpenDrawer} />
      </div>

      <InvoiceBatchTable
        data={items}
        handleDelete={handleDelete}
        handleViewBatchDetail={handleViewBatchDetail}
        handleSendRenewalNotices={handleSendRenewalNotices}
        handleAddToNetcashBatch={handleAddToNetcashBatch}
        currentPage={currentPage}
        totalPages={Math.ceil(totalRecords / pageSize)}
        setCurrentPage={setCurrentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isLoading={isLoading}
      />

      <AddToNetcashBatchDrawer
        isOpen={!!netcashDrawerBatch}
        onClose={() => setNetcashDrawerBatch(null)}
        subject={`every outstanding debit order invoice in the ${netcashDrawerBatch?.month ?? ''}/${netcashDrawerBatch?.year ?? ''} invoice batch`}
        defaultYear={netcashDrawerBatch?.year ?? new Date().getFullYear()}
        defaultMonth={netcashDrawerBatch?.month ?? new Date().getMonth() + 1}
        isSubmitting={isAddingToNetcashBatch}
        onConfirm={handleConfirmAddToNetcashBatch}
      />

      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        title="Create Invoice Batch"
        size="small"
      >
        <div className="flex flex-col gap-4">
          <FormField
            id="year"
            label="Year"
            type="number"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            placeholder="Enter Year"
            required={true}
          />
          <FormField
            id="month"
            label="Month"
            type="dropdown"
            value={selectedMonth.toString()}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            placeholder="Select Month"
            required={true}
            options={monthOptions}
          />
          <div className="flex gap-2 mt-2 justify-end">
            <Button
              onClick={handleCloseDrawer}
              variant="secondary"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              variant="primary"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Batch'}
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

const InvoiceBatchList: React.FC = () => {
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Invoice Batches', href: '#' },
  ];

  const tabs = [
    {
      id: 'invoicebatches',
      title: 'Invoice Batches',
      content: (
        <Card title="Manage Invoice Batches">
          <InvoiceBatchesPanel />
        </Card>
      ),
    },
    {
      id: 'netcashbatches',
      title: 'Netcash Batches',
      content: (
        <Card title="Netcash Batches">
          <NetcashBatchesPanel />
        </Card>
      ),
    },
  ];

  return (
    <AdminLayout
      activeMenuItem="/admin/invoicebatches"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Finance.InvoiceBatch.View"
    >
      <div className="h-full">
        <Tab tabs={tabs} defaultActiveTab="invoicebatches" />
      </div>
    </AdminLayout>
  );
};

export default InvoiceBatchList;
